import { bodyShapeRenderer, eyeBallRenderer, eyeFrameRenderer } from './qrShapes';

/**
 * Picker thumbnails. Body/eye thumbs are drawn by the *same* shape functions the
 * renderer uses, so a thumbnail can never drift from what the QR code produces.
 */

// A QR-ish 5×5 block: dense enough to show how connected styles behave.
const THUMB_PATTERN = [
    [1, 1, 0, 1, 1],
    [1, 0, 1, 0, 1],
    [0, 1, 1, 1, 0],
    [1, 0, 1, 0, 1],
    [1, 1, 0, 1, 1],
];

const patternDark = (x, y) => (
    y >= 0 && y < THUMB_PATTERN.length && x >= 0 && x < THUMB_PATTERN[0].length
        ? !!THUMB_PATTERN[y][x]
        : false
);

export function DotThumb({ style }) {
    const draw = bodyShapeRenderer(style);
    const parts = [];
    THUMB_PATTERN.forEach((row, y) => {
        row.forEach((on, x) => {
            if (!on) return;
            parts.push(draw(x, y, {
                n: patternDark(x, y - 1),
                s: patternDark(x, y + 1),
                e: patternDark(x + 1, y),
                w: patternDark(x - 1, y),
                ne: patternDark(x + 1, y - 1),
                nw: patternDark(x - 1, y - 1),
                se: patternDark(x + 1, y + 1),
                sw: patternDark(x - 1, y + 1),
            }));
        });
    });

    return (
        <svg width="42" height="42" viewBox="-0.2 -0.2 5.4 5.4" aria-hidden="true">
            <g fill="currentColor" stroke="none" dangerouslySetInnerHTML={{ __html: parts.join('') }} />
        </svg>
    );
}

export function EyeFrameThumb({ style }) {
    return (
        <svg width="42" height="42" viewBox="-0.3 -0.3 7.6 7.6" aria-hidden="true">
            <g
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0"
                dangerouslySetInnerHTML={{ __html: eyeFrameRenderer(style)() }}
            />
        </svg>
    );
}

export function EyeBallThumb({ style }) {
    return (
        <svg width="42" height="42" viewBox="-0.3 -0.3 3.6 3.6" aria-hidden="true">
            <g
                fill="currentColor"
                stroke="currentColor"
                strokeWidth="0"
                dangerouslySetInnerHTML={{ __html: eyeBallRenderer(style)() }}
            />
        </svg>
    );
}

// Stylised mini-code for the style presets: one finder plus a scatter of body
// modules, all drawn by the real renderers so a preset tile shows its shapes.
const PRESET_MODULES = [
    [6, 0], [7, 0], [9, 0], [6, 1], [8, 1], [10, 1], [7, 2], [9, 2], [10, 2],
    [6, 3], [8, 3], [0, 6], [2, 6], [3, 6], [1, 7], [4, 7], [0, 8], [2, 8],
    [3, 8], [1, 9], [3, 9], [0, 10], [2, 10], [4, 10],
    [6, 6], [7, 7], [9, 6], [10, 8], [6, 9], [8, 10], [9, 9], [7, 10], [10, 6],
];

const isPresetModule = (x, y) => PRESET_MODULES.some(([mx, my]) => mx === x && my === y);

export function PresetThumb({ preset, size = 44 }) {
    const drawBody = bodyShapeRenderer(preset.dotStyle);
    const gradientId = `qrPreset-${preset.id}`;
    const paint = preset.gradientEnabled ? `url(#${gradientId})` : preset.fgColor;

    const modules = PRESET_MODULES.map(([x, y]) => drawBody(x, y, {
        n: isPresetModule(x, y - 1),
        s: isPresetModule(x, y + 1),
        e: isPresetModule(x + 1, y),
        w: isPresetModule(x - 1, y),
        ne: isPresetModule(x + 1, y - 1),
        nw: isPresetModule(x - 1, y - 1),
        se: isPresetModule(x + 1, y + 1),
        sw: isPresetModule(x - 1, y + 1),
    })).join('');

    return (
        <svg width={size} height={size} viewBox="-1 -1 13 13" aria-hidden="true" style={{ borderRadius: 8 }}>
            {preset.gradientEnabled ? (
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
                        <stop offset="0" stopColor={preset.gradientFrom} />
                        <stop offset="1" stopColor={preset.gradientTo} />
                    </linearGradient>
                </defs>
            ) : null}
            <rect x="-1" y="-1" width="13" height="13" rx="1.4" fill={preset.bgColor} />
            <g fill={paint} stroke="none" dangerouslySetInnerHTML={{ __html: modules }} />
            <g transform="scale(0.714)" fill={paint} stroke={paint} strokeWidth="0" dangerouslySetInnerHTML={{ __html: eyeFrameRenderer(preset.eyeFrameStyle)() }} />
            <g transform="translate(1.43 1.43) scale(0.714)" fill={paint} stroke={paint} strokeWidth="0" dangerouslySetInnerHTML={{ __html: eyeBallRenderer(preset.eyeBallStyle)() }} />
        </svg>
    );
}

export function FrameThumb({ type }) {
    switch (type) {
        case 'simple':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="48" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="5" y="5" width="30" height="30" rx="1" fill="currentColor" opacity="0.15" />
                    <line x1="1" y1="38" x2="39" y2="38" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                    <text x="20" y="46" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'rounded':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="48" rx="9" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="6" y="6" width="28" height="28" rx="2" fill="currentColor" opacity="0.15" />
                    <line x1="4" y1="38" x2="36" y2="38" stroke="currentColor" strokeWidth="1" opacity="0.4" />
                    <text x="20" y="46" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'banner':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="37" width="38" height="12" fill="currentColor" />
                    <rect x="1" y="1" width="38" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="5" y="5" width="30" height="28" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="46" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'rounded-banner':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="48" rx="9" fill="currentColor" />
                    <rect x="3" y="3" width="34" height="34" rx="7" fill="#fff" />
                    <rect x="7" y="7" width="26" height="26" rx="2" fill="currentColor" opacity="0.2" />
                    <text x="20" y="46" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'badge':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="2" y="2" width="36" height="46" rx="4" fill="none" stroke="currentColor" strokeWidth="3" />
                    <rect x="2" y="2" width="36" height="9" rx="4" fill="currentColor" />
                    <rect x="7" y="14" width="26" height="22" rx="2" fill="currentColor" opacity="0.15" />
                    <text x="20" y="46" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'double':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="48" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                    <rect x="5" y="5" width="30" height="40" rx="1" fill="none" stroke="currentColor" strokeWidth="1" opacity="0.5" />
                    <rect x="9" y="9" width="22" height="22" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="42" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'pill':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="48" rx="19" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="8" y="8" width="24" height="24" rx="4" fill="currentColor" opacity="0.15" />
                    <text x="20" y="44" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'top-banner':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="13" fill="currentColor" />
                    <rect x="1" y="1" width="38" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="5" y="17" width="30" height="28" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="11" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'bold':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="2" y="2" width="36" height="46" rx="1" fill="none" stroke="currentColor" strokeWidth="4" />
                    <rect x="7" y="7" width="26" height="26" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="44" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'thin':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1.5" y="1.5" width="37" height="47" rx="1" fill="none" stroke="currentColor" strokeWidth="0.7" />
                    <rect x="6" y="6" width="28" height="28" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="45" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'dashed':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1.5" y="1.5" width="37" height="47" rx="1" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="4 2.5" />
                    <rect x="6" y="6" width="28" height="28" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="45" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'corners':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true" stroke="currentColor" strokeWidth="2.4" fill="none">
                    <path d="M2 11V2h9M29 2h9v9M38 39v9h-9M11 48H2v-9" />
                    <rect x="8" y="8" width="24" height="24" rx="1" fill="currentColor" stroke="none" opacity="0.15" />
                    <text x="20" y="44" textAnchor="middle" fontSize="7" fill="currentColor" stroke="none" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'banner-both':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="6" fill="currentColor" />
                    <rect x="1" y="37" width="38" height="12" fill="currentColor" />
                    <rect x="1" y="1" width="38" height="48" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="5" y="11" width="30" height="23" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="46" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'ribbon':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="48" rx="5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="6" y="6" width="28" height="26" rx="1" fill="currentColor" opacity="0.15" />
                    <rect x="7" y="32" width="26" height="10" rx="5" fill="currentColor" />
                    <text x="20" y="39" textAnchor="middle" fontSize="6" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'shadow':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="5" y="6" width="34" height="43" rx="3" fill="currentColor" />
                    <rect x="1" y="1" width="34" height="43" rx="3" fill="#fff" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="5" y="5" width="26" height="26" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="18" y="40" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'speech':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="42" rx="6" fill="currentColor" />
                    <path d="M11 42h9l-9 7z" fill="currentColor" />
                    <rect x="4" y="4" width="32" height="28" rx="4" fill="#fff" />
                    <rect x="8" y="8" width="24" height="20" rx="1" fill="currentColor" opacity="0.2" />
                    <text x="20" y="39" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'speech-right':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="42" rx="6" fill="currentColor" />
                    <path d="M29 42h-9l9 7z" fill="currentColor" />
                    <rect x="4" y="4" width="32" height="28" rx="4" fill="#fff" />
                    <rect x="8" y="8" width="24" height="20" rx="1" fill="currentColor" opacity="0.2" />
                    <text x="20" y="39" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'speech-center':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="42" rx="6" fill="currentColor" />
                    <path d="M15 42h10l-5 7z" fill="currentColor" />
                    <rect x="4" y="4" width="32" height="28" rx="4" fill="#fff" />
                    <rect x="8" y="8" width="24" height="20" rx="1" fill="currentColor" opacity="0.2" />
                    <text x="20" y="39" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'speech-outline':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <path
                        d="M7 1.5h26a5.5 5.5 0 0 1 5.5 5.5v30a5.5 5.5 0 0 1-5.5 5.5h-4.5l-9 7v-7H7a5.5 5.5 0 0 1-5.5-5.5V7A5.5 5.5 0 0 1 7 1.5z"
                        fill="#fff"
                        stroke="currentColor"
                        strokeWidth="1.6"
                    />
                    <rect x="7" y="7" width="26" height="22" rx="1" fill="currentColor" opacity="0.15" />
                    <text x="20" y="38" textAnchor="middle" fontSize="7" fill="currentColor" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'thought':
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="1" y="1" width="38" height="38" rx="9" fill="currentColor" />
                    <circle cx="13" cy="43" r="4" fill="currentColor" />
                    <circle cx="7" cy="48.5" r="2.2" fill="currentColor" />
                    <rect x="4" y="4" width="32" height="24" rx="6" fill="#fff" />
                    <rect x="8" y="7" width="24" height="18" rx="1" fill="currentColor" opacity="0.2" />
                    <text x="20" y="35" textAnchor="middle" fontSize="7" fill="#fff" fontWeight="bold">SCAN</text>
                </svg>
            );
        case 'none':
        default:
            return (
                <svg width="34" height="42" viewBox="0 0 40 50" aria-hidden="true">
                    <rect x="2" y="7" width="36" height="36" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 2" opacity="0.6" />
                    <line x1="9" y1="14" x2="31" y2="36" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
                    <line x1="31" y1="14" x2="9" y2="36" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />
                </svg>
            );
    }
}
